import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

// Session token is an httpOnly cookie managed by the backend; the browser never exposes it to scripts.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: when returning from Google OAuth, AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    localStorage.removeItem("lumis_token"); // one-time cleanup of the legacy storage location
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch((error) => {
        if (error?.response?.status !== 401) console.error("Session check failed:", error?.message || error);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const completeGoogle = async (session_id, invite_code, accepted_terms, account_type = "employee") => {
    const { data } = await api.post("/auth/google/session", { session_id, invite_code: invite_code || null, accepted_terms, account_type });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (error) { console.warn("Logout request failed (session may already be expired):", error?.message || error); }
    setUser(null);
  };

  // Short-lived session (12h): silently rotate the cookie while the app is open so active users stay signed in.
  useEffect(() => {
    if (!user) return undefined;
    const rotate = () => api.post("/auth/refresh").catch((error) => console.warn("Session refresh failed:", error?.message || error));
    const id = setInterval(rotate, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, completeGoogle, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
